import React, { useDeferredValue, useMemo } from 'react';

import { e2eMailApis } from '@apis/next/e2eMailApis';
import { DevLayout } from '@components/dev/DevLayout';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { observer } from 'mobx-react';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, common) => ({
  e2eMailWrapper: {
    ...common.flexColumn,
    padding: '1rem',
    maxHeight: '100%',
  },
  e2eMailContainer: {
    ...common.flexColumn,
    ...common.middleText,
    flex: 1,
    overflow: 'auto',
    padding: '0.5rem',
    rowGap: '0.5rem',
    '& > *:nth-child(odd)': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  e2eMailItem: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '0.5rem',
    padding: '0.5rem',
  },
}));

export const E2eMail = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const e2eMailQuery = e2eMailApis.useGet('/e2e/mail');

  const form = useForm(
    z.object({
      search: z.string(),
    }),
  );

  const { fields } = form.root;

  const searchText = useDeferredValue(getDecodeResult(fields.search.decodeResult) || '');

  const filtered = useMemo(
    () =>
      e2eMailQuery.data?.filter(
        (mail) =>
          mail.to.some((e) => e.includes(searchText)) ||
          mail.subject.includes(searchText) ||
          mail.body.includes(searchText),
      ),
    [searchText, e2eMailQuery.data],
  );

  return (
    <DevLayout>
      <QueryContainer queries={[e2eMailQuery]}>
        <div className={classes.e2eMailWrapper}>
          <div>
            {filtered?.length} / {e2eMailQuery.data?.length} item(s)
          </div>

          <div>
            <TextInput field={fields.search} />
          </div>

          <div className={classes.e2eMailContainer}>
            {filtered?.map((mail, i) => (
              <div className={classes.e2eMailItem} key={i}>
                <label className={classes.textGray600}>To:</label>
                <div>{mail.to.join(', ')}</div>

                <label className={classes.textGray600}>Subject:</label>
                <div>{mail.subject}</div>

                <label className={classes.textGray600}>Body:</label>
                <div style={{ whiteSpace: 'pre-wrap' }}>{mail.body}</div>

                <label className={classes.textGray600}>From Email:</label>
                <div>{mail.from_email}</div>
              </div>
            ))}
          </div>
        </div>
      </QueryContainer>
    </DevLayout>
  );
});
